import { useState, type PointerEvent as ReactPointerEvent } from 'react'
import { Helmet } from 'react-helmet-async'
import { Link } from 'react-router-dom'
import { AnimatePresence, motion, useMotionValue, useReducedMotion, useSpring, useTransform } from 'framer-motion'
import { storeConfig } from '../../store.config'
import { ROUTES } from '../../src/constants/routes.constants'
import { Divider, SiteFooter, StorefrontNav, useFadeUp } from '../../src/ui/components/storefront/chrome'
import { StoryOverlay } from './StoryOverlay'

/**
 * `/` — public landing page.
 *
 * "Nature Distilled" art direction. One screen: the warm hero (the
 * background image is supplied by MainLayout, single-sourced from
 * `brand.heroImagePath`) with the brand lockup, tagline, CTAs, the
 * "Launching soon" note, and a compact 3-benefit row — then the footer.
 * The brand story opens as a right-to-left OVERLAY (StoryOverlay), not a
 * scrolled section. All copy is dark (charcoal) for legibility over the photo.
 */

function BambooIcon(): JSX.Element {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden>
      <path d="M9 21V3M15 21V3" strokeLinecap="round" />
      <path d="M9 8h6M9 14h6" strokeLinecap="round" />
      <path d="M9 5c2 1 4 1 6 0M9 17c2 1 4 1 6 0" strokeLinecap="round" />
    </svg>
  )
}
function ShieldIcon(): JSX.Element {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden>
      <path d="M12 3l7 3v5c0 4.5-3 8-7 10-4-2-7-5.5-7-10V6l7-3z" strokeLinejoin="round" />
      <path d="M9 12l2 2 4-4" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  )
}
function LeafIcon(): JSX.Element {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden>
      <path d="M5 19c0-7 5-13 14-14 1 9-5 15-14 14z" strokeLinejoin="round" />
      <path d="M9 15c2-3 5-5 8-6" strokeLinecap="round" />
    </svg>
  )
}

function BenefitMini({
  icon,
  title,
  sub,
  index
}: {
  readonly icon: JSX.Element
  readonly title: string
  readonly sub: string
  /** Position in the row (0-based) — desyncs the idle float so they bob independently. */
  readonly index: number
}): JSX.Element {
  const fade = useFadeUp()
  const reduce = useReducedMotion() === true
  return (
    <motion.div
      variants={fade}
      whileHover={reduce ? {} : { y: -6, scale: 1.05, transition: { type: 'spring', stiffness: 300, damping: 20 } }}
      whileTap={reduce ? {} : { scale: 0.98 }}
      className="group flex cursor-default flex-col items-center text-center"
    >
      {/* Continuous gentle float ("hover around") — staggered per card so the
          three bob out of phase; disabled under reduced-motion. */}
      <motion.div
        className="flex flex-col items-center"
        animate={reduce ? { y: 0 } : { y: [0, -8, 0] }}
        transition={reduce ? { duration: 0 } : { duration: 4 + index * 0.6, repeat: Infinity, ease: 'easeInOut', delay: index * 0.7 }}
      >
        <span className="mb-2 flex h-10 w-10 items-center justify-center rounded-full bg-bg-primary/70 text-forest shadow-sm backdrop-blur-sm transition-all duration-300 group-hover:bg-forest/20 group-hover:text-forest group-hover:shadow-md motion-safe:group-hover:scale-110">
          {icon}
        </span>
        <p className="font-heading text-body text-charcoal leading-tight transition-colors duration-300 group-hover:text-forest">
          {title}
        </p>
        <p className="font-body text-micro text-charcoal" style={{ opacity: 0.75 }}>
          {sub}
        </p>
      </motion.div>
    </motion.div>
  )
}

/** Hero — content over the layout's background image. Dark copy throughout. */
function Hero({ onOpenStory }: { readonly onOpenStory: () => void }): JSX.Element {
  const fade = useFadeUp()
  // Pointer-parallax: the frosted hero panel tilts a few degrees toward the
  // cursor (home only). Springs smooth it; reduced-motion disables it.
  const reduce = useReducedMotion() === true
  const px = useMotionValue(0)
  const py = useMotionValue(0)
  const rotateY = useSpring(useTransform(px, [-0.5, 0.5], [7, -7]), { stiffness: 110, damping: 18 })
  const rotateX = useSpring(useTransform(py, [-0.5, 0.5], [-6, 6]), { stiffness: 110, damping: 18 })
  function onTilt(e: ReactPointerEvent<HTMLElement>): void {
    if (e.pointerType !== 'mouse') return // desktop cursor only — never tilt/jitter on touch scroll
    const r = e.currentTarget.getBoundingClientRect()
    px.set((e.clientX - r.left) / r.width - 0.5)
    py.set((e.clientY - r.top) / r.height - 0.5)
  }
  function resetTilt(): void {
    px.set(0)
    py.set(0)
  }
  const { name, nameScript, tagline } = storeConfig.brand
  const product = storeConfig.products.catalog[0]
  const age =
    product !== undefined && product.minAgeMonths !== null && product.maxAgeMonths !== null
      ? `${product.minAgeMonths}–${product.maxAgeMonths} months`
      : 'Newborn ready'
  const weight =
    product !== undefined && product.maxWeightKg !== null ? `up to ${product.maxWeightKg} kg` : null
  return (
    <section
      className="relative flex min-h-screen flex-col items-center justify-center px-5 pt-24 pb-16 text-center"
      style={{ perspective: 1200 }}
      onPointerMove={reduce ? undefined : onTilt}
      onPointerLeave={reduce ? undefined : resetTilt}
    >
      <motion.div
        className="relative z-10 mx-auto max-w-2xl rounded-[2.5rem] border border-border/40 bg-bg-primary/65 px-6 py-10 shadow-lg backdrop-blur-md sm:px-12 sm:py-14"
        style={reduce ? {} : { rotateX, rotateY }}
        initial="hidden"
        animate="show"
        variants={{ show: { transition: { staggerChildren: 0.1 } } }}
      >
        {nameScript !== null ? (
          <motion.h1
            variants={fade}
            className="font-kannada text-forest leading-none"
            style={{ fontSize: 'clamp(2.75rem, 11vw, 6.5rem)' }}
          >
            {nameScript}
          </motion.h1>
        ) : null}
        <motion.p
          variants={fade}
          className="mt-3 font-heading uppercase text-charcoal leading-tight"
          style={{ fontSize: 'clamp(1rem, 3vw, 1.6rem)', letterSpacing: '0.28em' }}
        >
          {name}
        </motion.p>
        <motion.p
          variants={fade}
          className="mt-2 font-body italic text-charcoal"
          style={{ fontSize: 'clamp(0.8rem, 1.6vw, 0.95rem)', letterSpacing: '0.08em' }}
        >
          — the rocking lap —
        </motion.p>
        <motion.div variants={fade} className="my-5">
          <Divider />
        </motion.div>
        <motion.p
          variants={fade}
          className="font-heading italic text-charcoal leading-snug"
          style={{ fontSize: 'clamp(1.125rem, 3.4vw, 2rem)' }}
        >
          {tagline}
        </motion.p>
        <motion.div variants={fade} className="mt-8 flex flex-col items-center gap-3 sm:flex-row sm:justify-center">
          <button
            type="button"
            onClick={onOpenStory}
            className="rounded-full bg-forest px-6 py-3 font-body text-body text-charcoal shadow-md transition hover:bg-forest/90 hover:shadow-lg"
          >
            Discover our story
          </button>
          <Link
            to={ROUTES.ACCOUNTREGISTER}
            className="rounded-full border border-border px-6 py-3 font-body text-body text-charcoal transition hover:border-forest hover:text-forest"
          >
            Create an account
          </Link>
        </motion.div>
        <motion.p
          variants={fade}
          className="mt-7 font-body italic text-charcoal"
          style={{ fontSize: '0.8rem', letterSpacing: '0.12em', opacity: 0.85 }}
        >
          Launching soon · Bengaluru
        </motion.p>
        <motion.div
          variants={{ hidden: {}, show: { transition: { staggerChildren: 0.12, delayChildren: 0.05 } } }}
          className="mx-auto mt-9 grid max-w-xl grid-cols-1 gap-6 sm:grid-cols-3 sm:gap-5"
        >
          <BenefitMini index={0} icon={<BambooIcon />} title="Rattan & bamboo" sub="Natural, breathable, handmade" />
          <BenefitMini index={1} icon={<ShieldIcon />} title="Made safe" sub={weight !== null ? `${age} · ${weight}` : age} />
          <BenefitMini index={2} icon={<LeafIcon />} title="Heirloom quality" sub="Built to be passed on" />
        </motion.div>
      </motion.div>
    </section>
  )
}

export function HomePage(): JSX.Element {
  const [storyOpen, setStoryOpen] = useState(false)
  const brand = storeConfig.brand
  return (
    <>
      <Helmet>
        <title>{brand.name} — {brand.tagline}</title>
        <meta name="description" content={brand.tagline} />
      </Helmet>
      <StorefrontNav showShop />
      <main>
        <Hero onOpenStory={() => setStoryOpen(true)} />
      </main>
      <SiteFooter />
      <AnimatePresence>
        {storyOpen ? <StoryOverlay onClose={() => setStoryOpen(false)} /> : null}
      </AnimatePresence>
    </>
  )
}
